<script setup>
import CloseButton from '@/components/shared/buttons/CloseButton.vue'
import CloseBar from '@/components/shared/buttons/CloseBar.vue'
import Icon from '@/components/shared/icons/Icon.vue'
import Heading from '@/components/shared/page/Heading.vue'
import {ref, toRefs, watch, nextTick} from 'vue'

//Emits and props
const emit = defineEmits([
  'open',
  'close',
])
const props = defineProps({
  header: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
  isFlexible: {
    type: Boolean,
    default: false,
  },
  isFull: {
    type: Boolean,
    default: false,
  },
  closeOnClickOutside: {
    type: Boolean,
    default: false,
  },
  closeOnKey: {
    type: Boolean,
    default: false,
  },
  labelClose: {
    type: String,
    default: 'Close',
  },
  overflow: {
    type: String,
    default: 'auto',
  },
})

//Modal ref and modal handlers
const {isOpen, closeOnClickOutside, closeOnKey} = toRefs(props)
const modal = ref(null)

//Watch open state
watch(isOpen, () => {
  if (isOpen.value) {
    emit('open')
    nextTick(() => modal.value?.showModal()) //HTMLDialogElement
  }
  else {
    nextTick(() => modal.value?.close()) //HTMLDialogElement
  }
})

//Handle close
const handleClose = (event, method) => {
  if (method === 'click' && closeOnClickOutside.value && event.target === modal.value) {
    emit('close', event)
  }
  else if (method === 'key' && closeOnKey.value) {
    emit('close', event)
  }
}
</script>

<template>
  <dialog
    v-if="isOpen"
    ref="modal"
    :class="{
      [$style.modal]: true,
      [$style.flexible]: isFlexible,
      [$style.full]: isFull,
    }"
    @click="handleClose($event, 'click')"
    @keydown.esc.prevent="handleClose($event, 'key')"
  >
    <div
      :class="{
        [$style.header]: true,
      }"
    >
      <div :class="$style.headerContent">
        <slot name="header">
          <slot name="icon">
            <Icon
              v-if="icon"
              size="l"
              :icon="icon"
              color="muted"
            />
          </slot>
          <Heading
            size="l"
            :text="header"
          />
        </slot>
      </div>
      <CloseButton
        :class="$style.close"
        test-id="closeModal"
        @click="$emit('close', $event)"
      />
    </div>

    <div
      :class="$style.body"
      :style="{overflow}"
    >
      <slot />
    </div>

    <div :class="$style.footer">
      <slot name="footer">
        <CloseBar
          :label-close="labelClose"
          @close="$emit('close')"
        />
      </slot>
    </div>
  </dialog>
</template>

<style module lang="scss">
.modal {
  outline: none;
  border: none;
  padding: 1rem 1.5rem;
  background: var(--bg-page-element);
  text-align: left;
  height: 100vh;
  width: 100vw;
  max-width: 100vw;
  max-height: 100vh;
  animation: zoom-in .3s;
  color: var(--color-text);
  overscroll-behavior: contain;
  &::backdrop {
    background: var(--color-black);
    animation: fade-in .3s;
    opacity: 0.3;
  }
  &[open] {
    display: flex;
    flex-direction: column;
  }

  //Desktop
  @media (min-width: 800px) {
    height: fit-content;
    box-shadow: var(--box-shadow-strong);
    border-radius: var(--border-radius-m);
    width: 600px;
    max-height: 90vh;
    max-width: 600px;
    &.flexible {
      width: fit-content;
      max-width: 90vw;
    }
    &.full {
      width: 90vw;
      max-width: 90vw;
      height: 90vh;
      .body {
        height: 100%;
      }
    }
  }
}
.header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.25rem 0;
}
.headerContent {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  h3 {
    margin: 0;
  }
}
.close {
  margin-top: -0.75rem;
  margin-right: -1rem;
}
.body {
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 1rem;
}
.footer {
  margin-top: 1rem;
  padding-bottom: 0.5rem;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.3;
  }
}

@keyframes zoom-in {
  from {
    opacity: 0;
    transform: scale(.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
